import { Component, EventEmitter, inject, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { CompositeArticleDetailModel } from 'src/app/models/preliminaries/composite-article-detail.model';
import { CompositeArticleModel } from 'src/app/models/preliminaries/composite-article.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { CompositeArticleDetailCaptureTableModel } from 'src/app/models/tables/preliminaries/composite-article-detail-capture-table.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { CompositeArticleDetailService } from 'src/app/services/preliminaries/composite-article-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-composite-article-detail',
  templateUrl: './composite-article-detail.component.html',
  styleUrl: './composite-article-detail.component.scss',
  standalone: false
})
export class CompositeArticleDetailComponent {

  private readonly compositeArticleDetailService = inject(CompositeArticleDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly confirmDialogService = inject(ConfirmDialogService);

  @ViewChild('articleSelection') articleForSelectionModalComponent?: CustomModalComponent | null = null;
  @Input() compositeArticleModel: CompositeArticleModel = new CompositeArticleModel();
  @Input() permissionUpdatable: boolean = false;
  @Output() emitQuantityDetails = new EventEmitter<number>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  componentTableSelection = CustomTableSelectComponent;
  dataArticle: PagedResponseModel<ArticleSelectionTableModel> =
    new PagedResponseModel<ArticleSelectionTableModel>();
  detailData: PagedResponseModel<CompositeArticleDetailCaptureTableModel> =
    new PagedResponseModel<CompositeArticleDetailCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  disabledButton: boolean = false;
  filters: ColumnResponseModel[] = [];
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: [],
  });
  inputsForImportDetailModals$ = new BehaviorSubject<any>({
    compositeArticleId: 0,
  });
  isFormView: boolean = false;
  limit: number = 10;
  limitArticle: number = 5;
  compositeArticleDetail: CompositeArticleDetailModel = new CompositeArticleDetailModel();
  compositeArticleDetails: CompositeArticleDetailModel[] = [];
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  quantityDetails: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [];

  async ngOnInit() {
    await this.getColumnsForDetailCapture();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeForSelection(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeForSelection(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    this.tableActions = this.getDefaultActions();
    const { compositeArticleModel } = changes;
    if (compositeArticleModel?.firstChange === false) {
      this.clearSelectionTrigger++;
      await this.getCompositeArticleDetailsForCapture();
    }
  }

  async deleteCompositeArticleDetail(selectedIds: number[]) {
    const accepted = await this.confirmDialogService.confirm(
      '¿Realmente desea eliminar el despiece del artículo compuesto?'
    );
    if (accepted) {
      try {
        await firstValueFrom(this.compositeArticleDetailService.deleteSomeDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El despiece del artículo compuesto fue eliminado exitosamente.'
        );
        this.clearSelectionTrigger++;
        await this.getCompositeArticleDetailsForCapture();
        this.getFormView(false);
      } catch (error) {
        console.error('Error al eliminar el despiece del artículo compuesto.', error);
      }
    }
  }

  async getArticlesForSelection() {
    try {
      this.dataArticle = await firstValueFrom(
        this.compositeArticleDetailService.getArticlesForSelection(
          this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle, this.filtersArticle)
      );
      this.updateInputsForSelection();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos.', error);
    }
  }

  async getColumnsForArticleSelection() {
    try {
      if (this.headerArticle.length === 0) {
        const response = await firstValueFrom(
          this.compositeArticleDetailService.getColumnsForArticleSelection()
        );
        this.headerArticle = response;
        this.updateInputsForSelection();
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getColumnsForDetailCapture() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(
          this.compositeArticleDetailService.getColumnsForDetailCapture()
        );
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas del detalle para la captura.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async getCompositeArticleDetailById(detailId: number) {
    try {
      const response = await firstValueFrom(
        this.compositeArticleDetailService.getCompositeArticleDetailById(detailId)
      );
      this.getFormView(true);
      this.compositeArticleDetail = response;
    } catch (error) {
      console.error('Error al obtener el despiece del artículo compuesto por ID.', error);
    }
  }

  async getCompositeArticleDetailByKeys(detailId: number) {
    try {
      const compositeArticleId = this.compositeArticleModel.articleId;
      const response = await firstValueFrom(
        this.compositeArticleDetailService.getCompositeArticleDetailByKeys(compositeArticleId, detailId)
      );
      this.getFormView(true);
      this.compositeArticleDetail = response;
    } catch (error) {
      console.error('Error al obtener el despiece del artículo compuesto por clave.', error);
    }
  }

  async getCompositeArticleDetailsForCapture() {
    try {
      const compositeArticleId = this.compositeArticleModel.articleId;
      this.detailData = await firstValueFrom(
        this.compositeArticleDetailService.getCompositeArticleDetailsForCapture(
          this.page, this.limit, this.columnSort, this.ascendant, this.filters, compositeArticleId
        )
      );
      this.quantityDetails = this.detailData.totalItems;
      this.emitQuantityDetails.emit(this.detailData.totalItems);
    } catch (error) {
      console.error('Error al obtener los despieces del artículo compuesto para la captura de datos.', error);
    }
  }

  getDefaultActions(): TableAction[] {
    if (!this.permissionUpdatable) {
      return [];
    }

    return [
      {
        label: 'Editar',
        icon: 'pi pi-pencil',
        action: 'edit'
      }
    ];
  }

  async handleSelectedItem(selectedItem: any[]) {
    if (selectedItem.length === 1) {
      await this.getCompositeArticleDetailByKeys(selectedItem[0].articleId);
      return;
    }

    this.compositeArticleDetails = selectedItem.map(({ compositeArticleDetailId, articleId }) => ({
      ...new CompositeArticleDetailModel(),
      compositeArticleId: this.compositeArticleModel.articleId,
      compositeArticleDetailId,
      articleId
    }));
  }

  async onAction(event: { action: string, item?: CompositeArticleDetailCaptureTableModel, selectedItems?: number[] }) {
    switch (event.action) {
      case 'edit': {
        await this.getCompositeArticleDetailById(event.item!.compositeArticleDetailId);
        break;
      }
      case 'bulk-delete': {
        if (event.selectedItems) {
          await this.deleteCompositeArticleDetail(event.selectedItems);
        }
        break;
      }
    }
  }

  async openArticleSelectionModal() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para la edición de artículo compuesto. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    if (!this.compositeArticleModel.articleId) {
      this.toastHandlerService.showToast(
        'warning',
        `El artículo compuesto aún no se ha guardado.`
      );
      return;
    }
    this.filtersArticle = [];
    await Promise.all([
      this.getColumnsForArticleSelection(),
      this.getArticlesForSelection()
    ]);
    this.articleForSelectionModalComponent?.show();
  }

  async onFilterChangeForSelection(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputsForSelection();
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.page = event.page;
    this.limit = event.pageSize;
    this.columnSort = event.sortField || null;
    this.ascendant = event.sortOrder === 1;
    await this.getCompositeArticleDetailsForCapture();
  }

  async onPageChangeForSelection(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    this.pageArticle = event.page;
    this.limitArticle = event.pageSize;
    this.columnSortArticle = event.sortField || null;
    this.ascendantArticle = event.sortOrder === 1;
    await this.getArticlesForSelection();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  async reload(reload: any) {
    if (reload) {
      await this.getCompositeArticleDetailsForCapture();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputsForSelection() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }
}
